import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit, Loader2, X, AlertCircle, CheckCircle, FileText, Eye, EyeOff, RotateCcw, ExternalLink, Check, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { pagesAPI, i18nAPI } from '../services/api';
import PageTranslationsPanel from './PageTranslationsPanel';
import { TARGET_LANGS } from './TranslationsEditor';
import { renderMarkdown } from '../services/markdown';

const EMPTY = { title: '', slug: '', group: 'legal', content: '', published: true, position: 0, i18n: {} };
const langsDone = (p) => TARGET_LANGS.filter(l => p.i18n?.[l.code]?.title && p.i18n?.[l.code]?.content).length;
const PLACEHOLDERS = ['name', 'email', 'phone', 'address', 'postal_code', 'city', 'siret', 'vat_number', 'website', 'vat_rate'];

const AdminPagesPanel = () => {
  const [pages, setPages] = useState([]);
  const [groups, setGroups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const untranslated = pages.filter(p => langsDone(p) < 4).length;

  useEffect(() => {
    if (!job?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await i18nAPI.status();
        const j = st.jobs?.pages;
        setJob(j || null);
        if (j && !j.running) {
          clearInterval(id);
          await load();
          setMessage({ type: j.errors?.length ? 'error' : 'success', text: `${j.done} page(s) traduites en 4 langues${j.errors?.length ? ` · ${j.errors.length} erreur(s) : ${j.errors.slice(0, 3).join(' ; ')}` : ''}` });
        }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(id);
  }, [job?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const translateAll = async () => {
    if (!window.confirm(`Traduire automatiquement (IA) le titre et le contenu des ${untranslated} page(s) non traduites en anglais, allemand, espagnol et italien ? Les traductions existantes sont conservées (tâche en arrière-plan).`)) return;
    setMessage(null);
    try {
      const r = await i18nAPI.translatePages(null, false);
      if (!r.started) { setMessage({ type: 'error', text: r.message }); return; }
      setJob(r.job);
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Traduction impossible' }); }
  };

  const load = useCallback(async () => {
    try {
      const data = await pagesAPI.adminList();
      setPages(data.pages);
      setGroups(data.groups);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, position: pages.length }); setPreview(false); setMessage(null); setEditing('new'); };
  const openEdit = (p) => { setForm({ title: p.title, slug: p.slug, group: p.group, content: p.content || '', published: p.published !== false, position: p.position || 0, i18n: { ...(p.i18n || {}) } }); setPreview(false); setMessage(null); setEditing(p); };

  const save = async () => {
    setMessage(null);
    if (form.title.trim().length < 2) { setMessage({ type: 'error', text: 'Le titre doit contenir au moins 2 caractères' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug.trim() || null, position: Number(form.position) || 0 };
      if (editing === 'new') await pagesAPI.create(payload);
      else await pagesAPI.update(editing.id, payload);
      await load();
      setEditing(null);
      setMessage({ type: 'success', text: `Page « ${form.title} » enregistrée` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (p) => {
    try {
      await pagesAPI.update(p.id, { title: p.title, slug: p.slug, group: p.group, content: p.content, published: !p.published, position: p.position || 0 });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur' });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la page « ${p.title} » ? Le lien disparaîtra du pied de page.`)) return;
    try {
      await pagesAPI.delete(p.id);
      await load();
      setMessage({ type: 'success', text: `Page « ${p.title} » supprimée` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const reset = async () => {
    if (!window.confirm('Remplacer le titre et le contenu par le texte par défaut ?')) return;
    try {
      const p = await pagesAPI.reset(editing.id);
      setForm(f => ({ ...f, title: p.title, content: p.content }));
      setMessage({ type: 'success', text: 'Contenu par défaut restauré (pensez à enregistrer)' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur' });
    }
  };

  const insertPlaceholder = (key) => setForm(f => ({ ...f, content: `${f.content}{{${key}}}` }));

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pages ({pages.length})</h1>
          <p className="text-sm text-gray-500">Mentions légales, CGV, livraison, retours… affichées dans le pied de page (Markdown).</p>
        </div>
        <Button data-testid="pages-translate-all-button" onClick={translateAll} disabled={job?.running} variant="outline" className="border-sky-400 text-sky-700 hover:bg-sky-50" title="Traduit via l'IA le titre et le contenu des pages en EN / DE / ES / IT">
          {job?.running ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Languages size={16} className="mr-2" />} {job?.running ? `Traduction… ${job.processed || 0}/${job.total || '?'}` : `Traduire les pages (IA)${untranslated ? ` (${untranslated})` : ''}`}
        </Button>
        <Button data-testid="admin-add-page-button" onClick={openNew} className="bg-orange-500 hover:bg-orange-600"><Plus size={18} className="mr-2" /> Nouvelle page</Button>
      </div>

      {message && !editing && (
        <p data-testid="pages-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : (
        <div className="space-y-6" data-testid="pages-groups">
          {Object.entries(groups).map(([key, label]) => {
            const list = pages.filter(p => p.group === key);
            return (
              <section key={key} data-testid={`pages-group-${key}`}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{label} <span className="text-gray-400 font-normal">({list.length})</span></h2>
                <div className="bg-white rounded-lg shadow-sm divide-y">
                  {list.length === 0 && <p className="p-4 text-sm text-gray-400 italic">Aucune page</p>}
                  {list.map(p => (
                    <div key={p.id} data-testid={`page-row-${p.slug}`} className="flex items-center gap-3 px-4 py-3">
                      <FileText size={16} className={p.published ? 'text-orange-500' : 'text-gray-300'} />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${p.published ? 'text-gray-900' : 'text-gray-400'}`}>{p.title}</p>
                        <p className="text-xs text-gray-400 truncate">/page/{p.slug} · {(p.content || '').length} car. · MAJ {p.updated_at ? new Date(p.updated_at).toLocaleDateString('fr-FR') : '—'}</p>
                      </div>
                      <span data-testid={`page-i18n-${p.slug}`} className={`hidden sm:inline text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${langsDone(p) === 4 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{langsDone(p) + 1}/5 langues</span>
                      <span className={`hidden sm:inline text-xs px-2 py-0.5 rounded-full ${p.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`page-status-${p.slug}`}>
                        {p.published ? 'Publiée' : 'Brouillon'}
                      </span>
                      <button data-testid={`page-toggle-${p.slug}`} onClick={() => togglePublished(p)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title={p.published ? 'Dépublier' : 'Publier'}>
                        {p.published ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                      <Link to={`/page/${p.slug}`} target="_blank" className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="Voir la page"><ExternalLink size={15} /></Link>
                      <button data-testid={`page-edit-${p.slug}`} onClick={() => openEdit(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit size={15} /></button>
                      <button data-testid={`page-delete-${p.slug}`} onClick={() => remove(p)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="page-form">
            <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2" data-testid="page-form-title">
                <FileText size={20} /> {editing === 'new' ? 'Nouvelle page' : `Modifier « ${editing.title} »`}
              </h2>
              <button onClick={() => setEditing(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {message && (
                <p data-testid="page-form-message" className={`text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                  {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Titre *</label>
                  <Input data-testid="page-title-input" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Mentions légales" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Colonne du pied de page</label>
                  <select data-testid="page-group-select" value={form.group} onChange={(e) => setForm(f => ({ ...f, group: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {Object.entries(groups).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Adresse (slug) — générée depuis le titre si vide</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">/page/</span>
                    <Input data-testid="page-slug-input" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="mentions-legales" />
                  </div>
                </div>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ordre</label>
                    <Input data-testid="page-position-input" type="number" min="0" value={form.position} onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))} />
                  </div>
                  <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
                    <input type="checkbox" data-testid="page-published-checkbox" checked={form.published} onChange={(e) => setForm(f => ({ ...f, published: e.target.checked }))} className="rounded border-gray-300 text-orange-500" />
                    Publiée
                  </label>
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <label className="block text-xs font-medium text-gray-600">Contenu (Markdown : ## Titre, **gras**, - liste, [lien](https://…))</label>
                  <div className="flex items-center gap-2">
                    {editing !== 'new' && (
                      <button type="button" data-testid="page-reset-button" onClick={reset} className="text-xs text-gray-500 hover:text-orange-600 inline-flex items-center gap-1"><RotateCcw size={12} /> Texte par défaut</button>
                    )}
                    <button type="button" data-testid="page-preview-toggle" onClick={() => setPreview(v => !v)} className={`text-xs px-2 py-1 rounded border ${preview ? 'bg-orange-500 text-white border-orange-500' : 'text-gray-600 border-gray-300 hover:border-orange-400'}`}>
                      {preview ? 'Éditer' : 'Aperçu'}
                    </button>
                  </div>
                </div>
                {preview ? (
                  <div data-testid="page-preview" className="prose-legal border border-gray-200 rounded-md p-4 min-h-[320px] text-sm text-gray-700 bg-gray-50" dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) }} />
                ) : (
                  <Textarea data-testid="page-content-input" value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} rows={16} className="font-mono text-sm" placeholder="## Titre de section&#10;Votre texte…" />
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-2 mb-4">
                  <span className="text-[11px] text-gray-400">Insérer une donnée société :</span>
                  {PLACEHOLDERS.map(k => (
                    <button key={k} type="button" data-testid={`page-placeholder-${k}`} onClick={() => insertPlaceholder(k)} className="text-[11px] font-mono bg-gray-100 hover:bg-orange-50 hover:text-orange-700 px-1.5 py-0.5 rounded">{`{{${k}}}`}</button>
                  ))}
                </div>
                <PageTranslationsPanel form={form} setForm={setForm} pageId={editing !== 'new' ? editing?.id : null} />
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="page-form-cancel">Annuler</Button>
              <Button data-testid="page-form-submit" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} {editing === 'new' ? 'Créer' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPagesPanel;
